import { Alert, Link } from '@amzn/awsui-components-core';
import constants from '../../../common/constants';
import {
  Content,
  ContentAudience,
  ContentType,
  ContentTypeMetadata,
} from '@amzn/awscat-pattern-library-service-model';
import CustomizedLink from '../../common/CustomizedLink';
import contactUrls from '../../../common/contactUrls';

const openAskQuestion = () => {
  window.open(contactUrls[constants.CONTACT_US_ADMIN_SUPPORT], '_blank');
};

const notAllowedToDeleteReviewerAlertMessage = (
  <>
    <span>You are not allowed to delete the peer reviewers for this version of publication. </span>
    <span>
      Please submit it to previously selected reviewers plus some additional reviewers you want to
      add.{' '}
    </span>
    <span>In case you want to delete any reviewers please contact </span>
    <Link onFollow={openAskQuestion}>APGL Administrator</Link>.
  </>
);

export const notAllowedToDeleteReviewerAlert = (
  <Alert>{notAllowedToDeleteReviewerAlertMessage}</Alert>
);

export const noReviewerAlert = <Alert type={'warning'}>{constants.PEER_REVIEWERS_MISSING}</Alert>;

export const hasCodeLinks = (contentTypeMetadata: ContentTypeMetadata) => {
  const { codeLinks } = contentTypeMetadata;
  return Array.isArray(codeLinks) && codeLinks.length > 0;
};

export const contentChecklistListName = 'ContentChecklist';

export const reviewerChecklistListName = 'ReviewerChecklist';

export const isForInternalContentAudience = (contentAudience: ContentAudience) =>
  contentAudience === ContentAudience.INTERNAL;

export const shouldResetContentChecklistDueToAudienceChange = (
  contentAudience: ContentAudience,
  updatedContentAudience: ContentAudience,
) => {
  const originalIsForInternalAudience = isForInternalContentAudience(contentAudience);
  const isForInternalAudience = isForInternalContentAudience(updatedContentAudience);
  return originalIsForInternalAudience !== isForInternalAudience;
};

export const shouldResetContentChecklistDueToContentTypeChange = (
  contentType: ContentType,
  updatedContentType: ContentType,
) => {
  if (!contentType) {
    return false;
  }
  return contentType !== updatedContentType;
};

export const shouldResetContentChecklistDueToHasCodeChange = (
  content: Content,
  updatedContent: Content,
) => {
  const { contentTypeMetadata: originalContentTypeMetadata } = content;
  const { contentTypeMetadata } = updatedContent;
  return hasCodeLinks(originalContentTypeMetadata) !== hasCodeLinks(contentTypeMetadata);
};

export const shouldResetChecklist = (
  content: Content,
  updatedContent: Content,
  contentAudience: ContentAudience,
  updatedContentAudience: ContentAudience,
) => {
  return (
    shouldResetContentChecklistDueToAudienceChange(contentAudience, updatedContentAudience) ||
    shouldResetContentChecklistDueToContentTypeChange(
      content.contentMetadata.contentType,
      updatedContent.contentMetadata.contentType,
    ) ||
    shouldResetContentChecklistDueToHasCodeChange(content, updatedContent)
  );
};

const awsWellArchitectPrinciplesHref =
  'https://aws.amazon.com/architecture/well-architected/?wa-lens-whitepapers.sort-by=item.additionalFields.sortDate&wa-lens-whitepapers.sort-order=desc';

const apgCodeGuidelinesHref =
  'https://apg-library.amazonaws.com/post-viewer/004867a6-8f11-4b9e-ac97-824dcea7aa38';

const contentSuggestionsHref = 'https://apg-library.amazonaws.com/content-suggestions';

const latestIconsHref = 'https://aws.amazon.com/architecture/icons/';

const brReviewGuideHref =
  'https://w.amazon.com/bin/view/AWS/Teams/GlobalServices/StrategyAndOperations/APGBarRaiser/BRReviewGuide/';

export const contentSuggestionsLink = (
  <CustomizedLink text="content suggestions" href={contentSuggestionsHref} />
);

export const awsWellArchitectPrinciplesLink = (
  <CustomizedLink text="AWS Well-Architect Principles" href={awsWellArchitectPrinciplesHref} />
);

export const apgCodeGuidelinesLink = (
  <CustomizedLink text="APG Code Guidelines" href={apgCodeGuidelinesHref} />
);

export const latestIconsLink = <CustomizedLink text="latest icons" href={latestIconsHref} />;

export const authorBrReviewGuideLink = (
  <CustomizedLink text="click here" href={brReviewGuideHref} />
);

export const reviewerBrReviewGuideLink = (
  <CustomizedLink text="listed here" href={brReviewGuideHref} />
);
